import { Card, Col, Row, Table, Tag, Typography } from 'antd'
import type { ReactNode } from 'react'

import { ClusterNameLink } from '@/components/common/ClusterNameLink'
import { TruncatedId } from '@/components/common/TruncatedId'
import { UserEmailLink } from '@/components/common/UserEmailLink'
import { formatUsdCents } from '@/utils/money'

import type { ChargeScope } from './chargeScope'
import {
  type AdminCharges,
  chargeAudience,
  chargesByCategory,
  chargesByCluster,
  chargesByPricingGroup,
  type ChargeTotal,
  toCents,
} from './chargeSummary'

const TOP_ACCOUNTS = 20

const chargedColumn = {
  title: 'Charged',
  dataIndex: 'cents',
  align: 'right' as const,
  render: formatUsdCents,
}

function TotalsCard({
  title,
  rows,
  label,
  extra,
}: {
  title: string
  rows: ChargeTotal[]
  label: (key: string) => ReactNode
  extra?: { title: string; render: (key: string) => string }
}) {
  return (
    <Card title={title} size="small" styles={{ body: { padding: 0 } }}>
      <Table
        size="small"
        dataSource={rows}
        rowKey="key"
        pagination={false}
        columns={[
          { title: 'Name', dataIndex: 'key', render: label },
          ...(extra
            ? [{ title: extra.title, dataIndex: 'key', render: extra.render }]
            : []),
          chargedColumn,
        ]}
      />
    </Card>
  )
}

export function ChargesTables({
  charges,
  scope,
}: {
  charges: AdminCharges
  scope: ChargeScope
}) {
  const accounts = charges.accounts.slice(0, TOP_ACCOUNTS)
  return (
    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
      <Col xs={24} lg={8}>
        <TotalsCard
          title="By product"
          rows={chargesByCategory(charges)}
          label={(key) => key}
        />
      </Col>
      <Col xs={24} lg={8}>
        <TotalsCard
          title="By cluster"
          rows={chargesByCluster(charges)}
          label={(key) =>
            key ? (
              <ClusterNameLink clusterId={key} />
            ) : (
              <Typography.Text type="secondary">
                No cluster (GPU, manual)
              </Typography.Text>
            )
          }
        />
      </Col>
      <Col xs={24} lg={8}>
        <TotalsCard
          title="By pricing group at the period"
          rows={chargesByPricingGroup(charges)}
          label={(key) => key || 'Unknown account'}
          extra={{ title: 'Counts as', render: chargeAudience }}
        />
      </Col>
      <Col xs={24}>
        <Card
          title={`Top ${TOP_ACCOUNTS} accounts`}
          extra={
            <Typography.Text type="secondary">
              By current pricing group
            </Typography.Text>
          }
          size="small"
          styles={{ body: { padding: 0 } }}
        >
          <Table
            size="small"
            dataSource={accounts}
            rowKey="subject"
            pagination={false}
            columns={[
              {
                title: 'Account',
                key: 'account',
                render: (_, row) =>
                  row.email ? (
                    <UserEmailLink userId={row.subject} email={row.email} />
                  ) : (
                    <TruncatedId id={row.subject} />
                  ),
              },
              {
                title: 'Current pricing group',
                dataIndex: 'pricingGroup',
                render: (group?: string | null) => (
                  <>
                    {group || '—'}
                    {scope === 'all' &&
                      chargeAudience(group) === 'Internal' && (
                        <Tag style={{ marginInlineStart: 8 }}>internal</Tag>
                      )}
                  </>
                ),
              },
              {
                ...chargedColumn,
                dataIndex: 'amountUsd',
                render: (amount: string) => formatUsdCents(toCents(amount)),
              },
            ]}
          />
        </Card>
      </Col>
    </Row>
  )
}
